import type { StateAction } from "@entrilia/shared/types";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import { isValidDate } from "@entrilia/shared/utilities/dateUtils";
import type {
  InvestorCommunication,
  UpdateInvestorCommunicationRequest,
} from "../../../../api/types/investorCommunicationsTypes";
import type { ArticleEditState, ArticleValidationErrors } from "./investorCommunicationArticleEditState";
import { articleEditModule } from "./investorCommunicationArticleEditState";
import type { VideoEditState, VideoValidationErrors } from "./investorCommunicationVideoEditState";
import { videoEditModule } from "./investorCommunicationVideoEditState";

export type InvestorCommunicationTypeEditState = ArticleEditState | VideoEditState;

export type InvestorCommunicationTypeValidationErrors = ArticleValidationErrors & VideoValidationErrors;

export type InvestorCommunicationTypeContentUpdate = Pick<
  UpdateInvestorCommunicationRequest,
  "articleContent" | "videoContent"
>;

export interface InvestorCommunicationTypeEditModule<S> {
  getInitialState: (savedCommunication: InvestorCommunication) => S;
  isContentChanged: (state: S, savedCommunication: InvestorCommunication) => boolean;
  isContentCompleted: (state: S, savedCommunication: InvestorCommunication) => boolean;
  validate: (
    state: S,
    savedCommunication: InvestorCommunication,
    isCompletionRequired: boolean
  ) => InvestorCommunicationTypeValidationErrors;
  getContentUpdate: (
    state: S,
    savedCommunication: InvestorCommunication
  ) => InvestorCommunicationTypeContentUpdate | undefined;
  getPartiallySavedState: (
    state: S,
    savedCommunication: InvestorCommunication,
    previousSavedCommunication: InvestorCommunication
  ) => S;
}

export interface InvestorCommunicationEditState {
  savedCommunication: InvestorCommunication;
  fundIds: string[];
  categoryIds: string[];
  title: string;
  description: string;
  typeState: InvestorCommunicationTypeEditState;
  publishedAt?: Date;
  isPublishedAtCustom: boolean;
  formVersion: number;
}

const typeEditModules = {
  Article: articleEditModule,
  Video: videoEditModule,
};

const withTypeEditModule = <R>(
  typeState: InvestorCommunicationTypeEditState,
  run: <S>(module: InvestorCommunicationTypeEditModule<S>, state: S) => R
): R => {
  switch (typeState.type) {
    case "Article":
      return run(articleEditModule, typeState);
    case "Video":
      return run(videoEditModule, typeState);
  }
};

const getPartiallySavedTypeState = (
  typeState: InvestorCommunicationTypeEditState,
  savedCommunication: InvestorCommunication,
  previousSavedCommunication: InvestorCommunication
): InvestorCommunicationTypeEditState => {
  switch (typeState.type) {
    case "Article":
      return articleEditModule.getPartiallySavedState(typeState, savedCommunication, previousSavedCommunication);
    case "Video":
      return videoEditModule.getPartiallySavedState(typeState, savedCommunication, previousSavedCommunication);
  }
};

const getSavedPublishedAt = ({ publishedAt }: InvestorCommunication): Date | undefined =>
  publishedAt === undefined ? undefined : new Date(publishedAt);

export const getInitialInvestorCommunicationEditState = (
  savedCommunication: InvestorCommunication,
  formVersion = 0
): InvestorCommunicationEditState => ({
  savedCommunication,
  fundIds: savedCommunication.fundIds,
  categoryIds: savedCommunication.categoryIds,
  title: savedCommunication.content?.title ?? "",
  description: savedCommunication.content?.description ?? "",
  typeState: typeEditModules[savedCommunication.type].getInitialState(savedCommunication),
  publishedAt: getSavedPublishedAt(savedCommunication),
  isPublishedAtCustom: savedCommunication.isPublishedAtCustom,
  formVersion,
});

export const isRecipientsSelectionChanged = ({
  savedCommunication,
  fundIds,
  categoryIds,
}: InvestorCommunicationEditState): boolean =>
  !arraysHaveSameItems(fundIds, savedCommunication.fundIds) ||
  !arraysHaveSameItems(categoryIds, savedCommunication.categoryIds);

export const isContentChanged = ({
  savedCommunication,
  title,
  description,
  typeState,
}: InvestorCommunicationEditState): boolean =>
  title.trim() !== (savedCommunication.content?.title ?? "") ||
  description.trim() !== (savedCommunication.content?.description ?? "") ||
  withTypeEditModule(typeState, (module, state) => module.isContentChanged(state, savedCommunication));

const isPublishedAtChanged = ({ savedCommunication, publishedAt }: InvestorCommunicationEditState): boolean =>
  publishedAt?.getTime() !== getSavedPublishedAt(savedCommunication)?.getTime();

const isPublishedAtCustomChanged = ({ savedCommunication, isPublishedAtCustom }: InvestorCommunicationEditState) =>
  isPublishedAtCustom !== savedCommunication.isPublishedAtCustom;

export const isDirty = (state: InvestorCommunicationEditState): boolean =>
  isRecipientsSelectionChanged(state) ||
  isContentChanged(state) ||
  isPublishedAtChanged(state) ||
  isPublishedAtCustomChanged(state);

export const isRecipientsStepCompleted = ({ fundIds, categoryIds }: InvestorCommunicationEditState): boolean =>
  fundIds.length > 0 && categoryIds.length > 0;

export const isContentStepCompleted = ({ savedCommunication, title, typeState }: InvestorCommunicationEditState) =>
  title.trim().length > 0 &&
  withTypeEditModule(typeState, (module, state) => module.isContentCompleted(state, savedCommunication));

export interface InvestorCommunicationValidationErrors extends InvestorCommunicationTypeValidationErrors {
  title?: string;
  fundIds?: string;
  categoryIds?: string;
  publishedAt?: string;
}

const titleRequiredError = "Title is required";
const fundsRequiredError = "Select at least one fund";
const categoriesRequiredError = "Select at least one category";

const getPublishedAtError = (state: InvestorCommunicationEditState): string | undefined => {
  const { publishedAt, isPublishedAtCustom } = state;

  if (publishedAt === undefined) {
    return isPublishedAtCustom || isPublishedAtChanged(state) ? "Publish date is required" : undefined;
  }

  if (!isPublishedAtChanged(state)) {
    return undefined;
  }

  if (!isValidDate(publishedAt)) {
    return "Enter a valid date";
  }

  return publishedAt.getTime() > Date.now() ? "Publish date can't be in the future" : undefined;
};

export const validateEditState = (state: InvestorCommunicationEditState): InvestorCommunicationValidationErrors => {
  const { savedCommunication, title, fundIds, categoryIds, typeState } = state;
  const isCompletionRequired = savedCommunication.status !== "Draft";
  const isTitleEmpty = title.trim().length === 0;

  return {
    title: isTitleEmpty && (isCompletionRequired || isContentChanged(state)) ? titleRequiredError : undefined,
    fundIds: isCompletionRequired && fundIds.length === 0 ? fundsRequiredError : undefined,
    categoryIds: isCompletionRequired && categoryIds.length === 0 ? categoriesRequiredError : undefined,
    publishedAt: getPublishedAtError(state),
    ...withTypeEditModule(typeState, (module, typeEditState) =>
      module.validate(typeEditState, savedCommunication, isCompletionRequired)
    ),
  };
};

export const isValid = (state: InvestorCommunicationEditState): boolean =>
  Object.values(validateEditState(state)).every((error) => error === undefined);

export const getUpdateInvestorCommunicationRequest = (
  state: InvestorCommunicationEditState
): UpdateInvestorCommunicationRequest | undefined => {
  const { savedCommunication, fundIds, categoryIds, title, description, typeState, publishedAt } = state;

  const request: UpdateInvestorCommunicationRequest = { expectedEtag: savedCommunication.etag };
  let hasChanges = false;

  if (!arraysHaveSameItems(fundIds, savedCommunication.fundIds)) {
    request.fundIds = fundIds;
    hasChanges = true;
  }

  if (!arraysHaveSameItems(categoryIds, savedCommunication.categoryIds)) {
    request.categoryIds = categoryIds;
    hasChanges = true;
  }

  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();

  if (
    trimmedTitle.length > 0 &&
    (trimmedTitle !== (savedCommunication.content?.title ?? "") ||
      trimmedDescription !== (savedCommunication.content?.description ?? ""))
  ) {
    request.content = { titleUserInput: trimmedTitle, descriptionUserInput: trimmedDescription || undefined };
    hasChanges = true;
  }

  const typeContentUpdate = withTypeEditModule(typeState, (module, typeEditState) =>
    module.getContentUpdate(typeEditState, savedCommunication)
  );

  if (typeContentUpdate) {
    Object.assign(request, typeContentUpdate);
    hasChanges = true;
  }

  if (isPublishedAtCustomChanged(state)) {
    request.isPublishedAtCustom = state.isPublishedAtCustom;
    hasChanges = true;
  }

  if (publishedAt && isPublishedAtChanged(state)) {
    request.publishedAt = publishedAt.toISOString();
    hasChanges = true;
  }

  return hasChanges ? request : undefined;
};

// Actions

export const setFundIdsAction =
  (fundIds: string[]): StateAction<InvestorCommunicationEditState> =>
  (state) => ({ ...state, fundIds });

export const setCategoryIdsAction =
  (categoryIds: string[]): StateAction<InvestorCommunicationEditState> =>
  (state) => ({ ...state, categoryIds });

export const setTitleAction =
  (title: string): StateAction<InvestorCommunicationEditState> =>
  (state) => ({ ...state, title });

export const setDescriptionAction =
  (description: string): StateAction<InvestorCommunicationEditState> =>
  (state) => ({ ...state, description });

export const setPublishedAtAction =
  (publishedAt: Date | undefined): StateAction<InvestorCommunicationEditState> =>
  (state) => ({ ...state, publishedAt });

// Automatic stamping ignores any date, and the API rejects one sent with the automatic flag.
export const setIsPublishedAtCustomAction =
  (isPublishedAtCustom: boolean): StateAction<InvestorCommunicationEditState> =>
  (state) => ({
    ...state,
    isPublishedAtCustom,
    publishedAt: isPublishedAtCustom ? state.publishedAt : getSavedPublishedAt(state.savedCommunication),
  });

export const discardChangesAction = (): StateAction<InvestorCommunicationEditState> => (state) =>
  getInitialInvestorCommunicationEditState(state.savedCommunication, state.formVersion + 1);

export const applySavedCommunicationAction =
  (savedCommunication: InvestorCommunication): StateAction<InvestorCommunicationEditState> =>
  (state) =>
    getInitialInvestorCommunicationEditState(savedCommunication, state.formVersion + 1);

export const applyPartiallySavedCommunicationAction =
  (savedCommunication: InvestorCommunication): StateAction<InvestorCommunicationEditState> =>
  (state) => ({
    ...getInitialInvestorCommunicationEditState(savedCommunication, state.formVersion + 1),
    typeState: getPartiallySavedTypeState(state.typeState, savedCommunication, state.savedCommunication),
  });
